import React, { useRef, useState } from 'react';
import {
  Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import dayjs from 'dayjs';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import PrescriptionTemplatePicker from './PrescriptionTemplatePicker';
import HiddenReport from '../../components/documents-templates/report/HiddenReport';
import PrescriptionReportTemplate from '../../components/documents-templates/PrescriptionReportTemplate';
import MealPlanWhatsAppButton from '../../pages/appointment/components/mealPlan/MealPlanWhatsAppButton';
import { buildReportFileName, generateReportPDF } from '../../services/clinicalReportService';
import parts from '../../pages/appointment/components/moduleCard/moduleParts.module.css';
import { DataProviderInstance } from '../../api/DataProvider';
import { findProfessional } from '../../components/documents-templates/report/reportCompany';
import {
  KIND_SIMPLE, KIND_SPECIAL, KIND_LABELS, MAX_COPIES, MAX_CONTENT_LENGTH, PRESCRIPTION_REPORT_TYPE, clampCopies, defaultIssuedAt, minCopies,
} from '../../pages/appointment/modules/prescription/prescriptionModel';
import styles from './PrescriptionPopup.module.css';

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Emissão da receita, como a de um documento: tipo (simples ou controle
 * especial), data, vias, profissional, assinatura digitalizada e o texto, com
 * os modelos da clínica. Salvar mantém o editor aberto para o PDF e o WhatsApp,
 * que saem do que está na tela.
 *
 * `prescription`: receita em edição ou null (nova).
 * `professionalId`: profissional do agendamento, padrão da nova receita.
 * `templates`: modelos da clínica; `onTemplatesChange(list)`.
 * `readOnly`: só leitura (aba Saúde), com PDF e WhatsApp.
 * `OnHide(needsReload)`.
 */
function PrescriptionPopup({
  patientId,
  patientName,
  appointmentId,
  appointmentDate,
  professionalId,
  prescription,
  templates,
  onTemplatesChange,
  readOnly = false,
  OnHide,
}) {
  const { CompanyConfig, SetPatientPrescription, DeletePatientPrescription, GetPatientById } = DataProviderInstance();
  const resources = Array.isArray(CompanyConfig?.Resources) ? CompanyConfig.Resources : [];

  const [kind, setKind] = useState(prescription ? prescription.kind : KIND_SIMPLE);
  const [issuedAt, setIssuedAt] = useState(prescription ? prescription.issuedAt : defaultIssuedAt(appointmentDate));
  const [copies, setCopies] = useState(prescription ? clampCopies(prescription.kind, prescription.copies) : 1);
  const [professional, setProfessional] = useState(
    String((prescription ? prescription.professionalId : professionalId) ?? '')
  );
  const [signed, setSigned] = useState(prescription ? Number(prescription.signed) === 1 : true);
  const [content, setContent] = useState(prescription ? prescription.content : '');
  const [templateList, setTemplateList] = useState(Array.isArray(templates) ? templates : []);
  const [current, setCurrent] = useState(prescription || null);
  const [needsReload, setNeedsReload] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });
  const selectedProfessional = findProfessional(CompanyConfig, professional);
  const hasSignature = !!(selectedProfessional && selectedProfessional.Signature);
  const exportRef = useRef(null);
  const patient = { name: patientName, ...(GetPatientById(patientId) || {}) };
  const draft = {
    kind,
    issuedAt,
    copies: clampCopies(kind, copies),
    signed: signed && hasSignature ? 1 : 0,
    content: content.trim(),
  };
  const canExport = !!issuedAt && draft.content !== '';

  function exportPdf() {
    if (exportRef.current) generateReportPDF(exportRef.current, buildReportFileName(patient.name, PRESCRIPTION_REPORT_TYPE));
  }

  function changeKind(value) {
    if (!value) return;
    setKind(value);
    setCopies((current) => clampCopies(value, value === KIND_SPECIAL ? Math.max(current, 2) : current));
  }

  function insertTemplate(text) {
    const value = String(text || '');
    setContent((current) => (current.trim() === '' ? value : `${current.replace(/\s+$/, '')}\n\n${value}`).slice(0, MAX_CONTENT_LENGTH));
  }

  function templatesChanged(list) {
    setTemplateList(list);
    if (onTemplatesChange) onTemplatesChange(list);
  }

  function saveHandler() {
    if (saving) return;
    if (!issuedAt) {
      showToast('Informe a data da receita.');
      return;
    }
    if (content.trim() === '') {
      showToast('Escreva a prescrição.');
      return;
    }

    setSaving(true);
    const payload = { id: current ? current.id : undefined, professionalId: professional, ...draft };
    SetPatientPrescription(patientId, current ? current.appointmentId : appointmentId, payload, (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        if (response.data) setCurrent(response.data);
        setNeedsReload(true);
        showToast('Receita salva.', 'success');
      } else {
        showToast(response?.error || 'Não foi possível salvar a receita.');
      }
    });
  }

  function deleteHandler() {
    setShowConfirmDelete(false);
    DeletePatientPrescription(current.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir a receita.');
      }
    });
  }

  const body = (
    <div className={styles.container}>
      <div className={styles.row}>
        <ToggleButtonGroup size="small" exclusive value={kind} disabled={readOnly} onChange={(event, value) => changeKind(value)}>
          {[KIND_SIMPLE, KIND_SPECIAL].map((key) => (
            <ToggleButton key={key} value={key} className={parts.toggleButton}>{KIND_LABELS[key]}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data"
            value={issuedAt ? dayjs(issuedAt) : null}
            format="DD/MM/YYYY"
            disabled={readOnly}
            onChange={(value) => setIssuedAt(value && value.isValid() ? value.format(DATE_FORMAT) : null)}
            slotProps={{ textField: { size: 'small', className: styles.date } }}
          />
        </LocalizationProvider>

        <FormControl size="small" className={styles.copies} disabled={readOnly}>
          <InputLabel>Vias</InputLabel>
          <Select label="Vias" value={copies} onChange={(event) => setCopies(clampCopies(kind, event.target.value))}>
            {Array.from({ length: MAX_COPIES }, (_, index) => index + 1)
              .filter((value) => value >= minCopies(kind))
              .map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" className={styles.professional} disabled={readOnly}>
          <InputLabel>Profissional</InputLabel>
          <Select label="Profissional" value={professional} onChange={(event) => setProfessional(String(event.target.value))}>
            {resources.map((item) => {
              const id = String(item.Id ?? item.id);
              return <MenuItem key={id} value={id}>{item.Label}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </div>

      {kind === KIND_SPECIAL && (
        <span className={styles.hint}>
          Sai em duas vias, uma retida na farmácia e outra de orientação ao paciente, com os quadros do emitente,
          do comprador e do fornecedor, como pede a Portaria SVS/MS 344/98.
        </span>
      )}

      {!readOnly && (
        <PrescriptionTemplatePicker
          templates={templateList}
          onTemplatesChange={templatesChanged}
          onInsert={insertTemplate}
          content={content}
          onError={showToast}
        />
      )}

      <TextField
        label="Prescrição"
        value={content}
        onChange={(event) => setContent(event.target.value.slice(0, MAX_CONTENT_LENGTH))}
        multiline
        disabled={readOnly}
        minRows={10}
        maxRows={22}
        fullWidth
        placeholder={'Medicamento, concentração e apresentação\nQuantidade\nModo de usar'}
      />

      <FormControlLabel
        control={<Checkbox checked={signed && hasSignature} disabled={readOnly || !hasSignature} onChange={(event) => setSigned(event.target.checked)} />}
        label={hasSignature ? 'Assinar com a assinatura digitalizada do profissional' : 'O profissional não tem assinatura digitalizada cadastrada'}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />

      {canExport && (
        <HiddenReport>
          <PrescriptionReportTemplate
            ref={exportRef}
            prescription={draft}
            companyData={CompanyConfig}
            professional={selectedProfessional}
            patient={patient}
          />
        </HiddenReport>
      )}
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {current && !readOnly && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
        />
      )}
      <CustomButton
        variant="outlined"
        style="secondary"
        label="PDF"
        icon={<PictureAsPdfOutlinedIcon />}
        onClick={exportPdf}
        enabled={canExport}
      />
      {canExport && (
        <MealPlanWhatsAppButton
          patientId={patientId}
          patientName={patient.name}
          getElement={() => exportRef.current}
          reportType={PRESCRIPTION_REPORT_TYPE}
          caption="Segue a sua receita."
          successMessage="Receita enviada por WhatsApp."
          errorMessage="Não foi possível enviar a receita por WhatsApp. Tente novamente."
          pdfErrorMessage="Não foi possível gerar o PDF da receita."
        />
      )}
      <CustomButton variant="outlined" style="secondary" label="Fechar" onClick={() => OnHide(needsReload)} enabled={!saving} />
      {!readOnly && (
        <CustomButton
          style="primary"
          variant="contained"
          label={current ? 'Atualizar' : 'Salvar receita'}
          icon={<IconSave />}
          onClick={saveHandler}
          loading={saving}
          enabled={!saving}
        />
      )}

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir esta receita?"
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={readOnly ? 'Receita' : current ? 'Editar receita' : 'Emitir receita'}
      Content={body}
      Footer={footer}
      OnClose={() => OnHide(needsReload)}
      maxWidth="md"
    />
  );
}

export default PrescriptionPopup;
